'use client'

import { useAuthProgress } from '@/feature/account/hooks/useAuthProgress'
import { ACCOUNT_DRAWER_HANDLE } from '@/feature/account/services/account.constants'
import { saveSignupEmailToStorage } from '@/feature/account/services/account.register'
import { toLogout } from '@/feature/auth/services/auth.routing'
import { useSiteConfig } from '@/feature/config/hooks/useSiteConfig.client'
import { useRefetchMiniCartButton } from '@/feature/mini-cart/hooks/useRefetchMiniCartTotal'
import { useRefreshProductListPriceAndStock } from '@/feature/product-list/hooks/useRefreshProductListPriceAndStocks'
import { useRefreshProductBuyBoxPriceAndStock } from '@/feature/product/hooks/useRefreshProductBuyBoxPriceAndStock'
import { useRefreshReferencesProductTableRowsPriceAndStock } from '@/feature/product/hooks/useRefreshReferencesProductTableRowsPriceAndStock'
import { clearSignInTrackingFromStorage } from '@/feature/tracking/services/tracking.unregister'
import { APOLLO_CLIENT_SIDE_OPTIONS } from '@/lib/apollo/clients/apolloClient.client'
import { useIsAuthenticated } from '@/lib/auth/client/AuthProvider.client'
import { useTranslation } from '@/lib/i18n/useTranslation.client'
import { useRouter } from '@/lib/navigation/PageNavigation.client'
import { useIsImpersonating } from '@/lib/next/authentication/impersonating.client'
import { useNextIsClientHydrated } from '@/lib/next/useNextIsClientHydrated'
import { pushToGtmDataLayer } from '@/lib/tracking/services/tracking.gtm'
import { Drawer, DrawerAction, DrawerBody, DrawerHeading } from '@/ui/drawer/drawer/Drawer.client'
import { Icon } from '@/ui/icons/icon/Icon'
import { useLazyQuery, useQuery } from '@apollo/client'
import { gql } from '@gql-generated/gql'
import {
  AccountDrawer_CurrentUserDocument,
  AddAccountLink_CurrentUserDocument,
  CompanyListCurrentUserAccountsDocument,
} from '@gql-generated/graphql'
import { useReactiveGetCookie } from 'cookies-next'
import { useCallback, useEffect, useMemo, useTransition } from 'react'
import {
  toAccountSelectionPage,
  toCompanyNationalIdPage,
  toHomePage,
  toPointOfSaleSelectionPage,
} from '../../services/account.routing'
import { selectUserAction, type SelectUserActionInput } from '../../services/user.action'
import { CompanyList, useRefreshApprovalFlowCurrentUser } from '../company-list/CompanyList.client'
import { NavigationHub } from '../navigation-hub/NavigationHub'
import { useIsAccountDrawerOpen, useSetAccountOpenDrawer } from './AccountDrawer.store'

gql(/* GraphQL */ `
  query AddAccountLink_CurrentUser {
    currentUser {
      id
      email
    }
  }
`)

// #region Actions

function AccountDrawerCreateAccountAction() {
  const { t } = useTranslation(['companyList'])
  const { config } = useSiteConfig()
  const setIsAccountDrawerOpen = useSetAccountOpenDrawer()
  const isImpersonating = useIsImpersonating()
  const { resetStep } = useAuthProgress()
  const router = useRouter()
  const { data } = useQuery(AddAccountLink_CurrentUserDocument, APOLLO_CLIENT_SIDE_OPTIONS)
  const isAccountCreationEnabled = config?.customer?.isAccountCreationEnabled ?? false

  useEffect(() => {
    if (data?.currentUser?.email) {
      saveSignupEmailToStorage(data.currentUser.email)
    }
  }, [data?.currentUser?.email])

  if (!isAccountCreationEnabled || isImpersonating) {
    return null
  }

  return (
    <DrawerAction
      handle="create-account"
      variant="secondary"
      icon={<Icon.UserPlus />}
      type="custom"
      action={async () => {
        pushToGtmDataLayer({
          event: 'gaEvent',
          eventCategory: 'parcours DOA DOC connexion',
          eventAction: 'ajout compte client multicompte',
          eventLabel: 'clic layer multicomptes ajouter un compte',
        })
        resetStep()
        setIsAccountDrawerOpen(false)
        router.push(toCompanyNationalIdPage)
      }}
    >
      {t('companyList:addAnAccount')}
    </DrawerAction>
  )
}

function AccountDrawerAction() {
  const { t } = useTranslation(['account', 'accountDrawer'])
  const isImpersonating = useIsImpersonating()
  const isHydrated = useNextIsClientHydrated()

  const handleSignOut = useCallback(function () {
    clearSignInTrackingFromStorage()

    pushToGtmDataLayer({
      event: 'gaEvent',
      eventCategory: 'layer mon compte',
      eventAction: 'clic cta',
      eventLabel: 'se déconnecter',
    })

    // Redirect to the API logout route to perform cookie clearance and
    // browser redirect without interrupting client-side Next.js/React streams
    window.location.href = toLogout
  }, [])

  if (!isHydrated || isImpersonating) {
    return null
  }

  return (
    <DrawerAction
      handle="sign-out"
      variant="primary"
      icon={<Icon.Unplug />}
      type="custom"
      action={handleSignOut}
    >
      {t('accountDrawer:signOut')}
    </DrawerAction>
  )
}

// #endregion Actions

gql(/* GraphQL */ `
  query AccountDrawer_QuoteNotificationConfig {
    config {
      quotation {
        isQuotationEnabled
      }
    }
  }

  query AccountDrawer_UserPermissions {
    currentUser {
      permissions {
        canManageQuote
      }
    }
  }

  query AccountDrawer_CurrentUser {
    currentUser {
      id
      firstname
      email
      hasProcessingQuotes
      permissions {
        hasAccessToWebOrders
      }
      company {
        id
        code
        name
        pointOfSale {
          name
          phoneNumber
          id
          address {
            line1
            city
          }
          subsidiary {
            id
          }
        }
      }
    }
  }
`)

export function AccountDrawer() {
  const isOpen = useIsAccountDrawerOpen()
  const setIsAccountDrawerOpen = useSetAccountOpenDrawer()
  const isAuthenticated = useIsAuthenticated()
  const { t } = useTranslation(['account', 'accountDrawer'])
  const router = useRouter()
  const [_isPending, startTransition] = useTransition()

  const refreshProductBuyBoxPriceAndStock = useRefreshProductBuyBoxPriceAndStock()
  const refreshReferencesProductTableRows_PriceAndStock =
    useRefreshReferencesProductTableRowsPriceAndStock()
  const refreshProductListPriceAndStocks = useRefreshProductListPriceAndStock()
  const refetchMiniCart = useRefetchMiniCartButton({ callOnMount: false })
  const refreshApprovalFlowCurrentUser = useRefreshApprovalFlowCurrentUser()

  const getCookie = useReactiveGetCookie()
  const companyCode = getCookie('company-code') // will update when server action updates cookie

  // -- Current user and accounts data

  const [
    fetchCurrentUser,
    {
      data: currentUserData,
      loading: loadingCurrentUser,
      error: currentUserError,
      refetch: refetchCurrentUser,
    },
  ] = useLazyQuery(AccountDrawer_CurrentUserDocument)

  const [
    fetchAccounts,
    {
      data: accountsData,
      loading: loadingAccounts,
      error: accountsError,
      refetch: refetchAccounts,
    },
  ] = useLazyQuery(CompanyListCurrentUserAccountsDocument)

  useEffect(() => {
    if (currentUserError) {
      console.error('Error fetching current user for account drawer:', currentUserError)
    }
  }, [currentUserError])

  useEffect(() => {
    if (accountsError) {
      console.error('Error fetching user accounts for account drawer:', accountsError)
    }
  }, [accountsError])

  const currentUser = currentUserData?.currentUser

  useEffect(() => {
    if (isOpen && isAuthenticated) {
      fetchCurrentUser()
      fetchAccounts()
    }
  }, [fetchCurrentUser, isOpen, fetchAccounts, isAuthenticated])

  useEffect(() => {
    if (isOpen && companyCode) {
      refetchCurrentUser()
      refetchAccounts()
    }
  }, [isOpen, companyCode, refetchAccounts, refetchCurrentUser])

  // -- Sorted accounts based

  const sortedAccounts = useMemo(() => {
    // Account with companyCode equals to the one in cookie should comes FIRST
    return [...(accountsData?.currentUserAccounts ?? [])].sort((a, b) => {
      if (a.company?.code === companyCode) return -1
      if (b.company?.code === companyCode) return 1

      return 0
    })
  }, [accountsData, companyCode])

  // -- Handlers

  const handleAccountSelected = useCallback(
    async (account: SelectUserActionInput): Promise<void> => {
      pushToGtmDataLayer({
        event: 'gaEvent',
        eventCategory: 'layer mon compte',
        eventAction: 'clic compte',
        eventLabel: 'changement de compte',
      })
      setIsAccountDrawerOpen(false)
      startTransition(async () => {
        await selectUserAction(account)

        // Manual refreshes (mostly useful if we stayed on the page, but good to have as fallback)
        refreshProductBuyBoxPriceAndStock()
        refreshReferencesProductTableRows_PriceAndStock()
        refreshProductListPriceAndStocks()
        refetchMiniCart()
        refreshApprovalFlowCurrentUser()

        const isSelectionPage = window.location.pathname === toAccountSelectionPage
        const hasFavoritePointOfSale = Boolean(account.company.favoritePointOfSaleId)
        const path = hasFavoritePointOfSale
          ? isSelectionPage
            ? toHomePage
            : window.location.pathname + window.location.search
          : toPointOfSaleSelectionPage

        router.push(path)
      })
    },
    [
      setIsAccountDrawerOpen,
      refreshProductBuyBoxPriceAndStock,
      refreshReferencesProductTableRows_PriceAndStock,
      refreshProductListPriceAndStocks,
      refetchMiniCart,
      refreshApprovalFlowCurrentUser,
      router,
      startTransition,
    ],
  )

  const loading = loadingCurrentUser || loadingAccounts

  return (
    <Drawer
      isOpen={isOpen}
      isLoading={loading}
      onOpenChange={setIsAccountDrawerOpen}
      handle={ACCOUNT_DRAWER_HANDLE}
      title={
        currentUser && !loading ? `${t('accountDrawer:hello')} ${currentUser.firstname}` : undefined
      }
      subtitle={currentUser?.email ?? undefined}
    >
      <DrawerHeading>{currentUser && !loading && <NavigationHub />}</DrawerHeading>

      <DrawerBody>
        <>
          {!loading && (
            <CompanyList
              accounts={sortedAccounts}
              activeCompanyCode={companyCode}
              onAccountSelected={handleAccountSelected}
            />
          )}
        </>
      </DrawerBody>

      <AccountDrawerCreateAccountAction />
      <AccountDrawerAction />
    </Drawer>
  )
}
